/**
 * Due-date arithmetic shared by the wall, the crew app and the office
 * (pure: no database import, safe in client components).
 *
 * The `tasks.due_date` column is epoch milliseconds. A shop task's due date
 * is a calendar day in Central time, so it is stored at 18:00 UTC — midday
 * Central whether or not daylight time is in effect — and read back through
 * the America/Chicago calendar. Both directions land on the same day.
 */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** `YYYY-MM-DD` Central calendar date → epoch ms at Central midday. */
export function dueDateToMs(dueDate: string): number {
  if (!DATE_RE.test(dueDate)) throw new Error(`invalid due date: ${dueDate}`);
  const ms = Date.parse(`${dueDate}T18:00:00Z`);
  if (Number.isNaN(ms)) throw new Error(`invalid due date: ${dueDate}`);
  return ms;
}

/** Epoch ms → `YYYY-MM-DD` on the Central calendar. */
export function centralDateOfMs(ms: number): string {
  return new Date(ms).toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
}

/** Today's Central calendar date. */
export function todayCentral(now: Date = new Date()): string {
  return centralDateOfMs(now.getTime());
}

/** Shift a `YYYY-MM-DD` by whole days. */
export function addDaysToDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Whole days from `today` to `due`; negative when overdue. */
export function daysUntil(due: string, today: string): number {
  const dayMs = 86_400_000;
  return Math.round((Date.parse(`${due}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / dayMs);
}

export interface DueText {
  text: string;
  overdue: boolean;
}

/**
 * "today", "tomorrow", "Fri", "Oct 20", "yesterday" or "3 days ago" for a
 * due date against a board date. Weekday names within the week, month and
 * day beyond it.
 */
export function dueTextForDate(due: string, today: string): DueText {
  const diffDays = daysUntil(due, today);
  if (diffDays === 0) return { text: 'today', overdue: false };
  if (diffDays === 1) return { text: 'tomorrow', overdue: false };
  if (diffDays < 0) {
    const n = -diffDays;
    return { text: n === 1 ? 'yesterday' : `${n} days ago`, overdue: true };
  }
  if (diffDays < 7) {
    return {
      text: new Date(`${due}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
      overdue: false,
    };
  }
  return {
    text: new Date(`${due}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
    overdue: false,
  };
}

/** Same as `dueTextForDate`, for a stored epoch-ms due date. */
export function dueLabel(dueAtMs: number, today: string): DueText {
  return dueTextForDate(centralDateOfMs(dueAtMs), today);
}
